import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams, Navigate } from 'react-router-dom';
import DoctorProfile from './DoctorProfile';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

/**
 * SlugResolver - Resolves custom slugs to doctor/clinic profiles
 *
 * This component handles URLs like /dr-sarah-jones and determines whether
 * the slug belongs to a doctor or clinic, then renders the appropriate profile.
 */
const SlugResolver = () => {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const [resolvedData, setResolvedData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const resolveSlug = async () => {
      if (!slug) {
        setNotFound(true);
        setIsLoading(false);
        return;
      }

      try {
        // Try to resolve as a doctor slug first
        const doctorResponse = await fetch(`${API_URL}/doctors/slug/${encodeURIComponent(slug)}`);

        if (doctorResponse.ok) {
          const doctorData = await doctorResponse.json();
          setResolvedData({ type: 'doctor', data: doctorData, id: doctorData.id });
          setIsLoading(false);
          return;
        }

        // If not a doctor, try clinic
        const clinicResponse = await fetch(`${API_URL}/clinics/slug/${encodeURIComponent(slug)}`);

        if (clinicResponse.ok) {
          const clinicData = await clinicResponse.json();
          setResolvedData({ type: 'clinic', data: clinicData, id: clinicData.id });
          setIsLoading(false);
          return;
        }

        // Slug not found
        setNotFound(true);
      } catch (error) {
        console.error('Error resolving slug:', error);
        setNotFound(true);
      } finally {
        setIsLoading(false);
      }
    };

    resolveSlug();
  }, [slug]);

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-light-bg dark:bg-obsidian flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-light-text dark:border-pure-white mx-auto mb-4"></div>
          <p className="font-mono text-sm uppercase tracking-widest text-light-text-secondary dark:text-silver/60">
            Loading...
          </p>
        </div>
      </div>
    );
  }

  // Not found - redirect to 404
  if (notFound) {
    return <Navigate to="/404" replace />;
  }

  // Resolved as doctor - redirect to doctor profile with the ID
  if (resolvedData?.type === 'doctor') {
    // Add source=link-in-bio to track that this booking came from link-in-bio page
    const newParams = new URLSearchParams(searchParams);
    newParams.set('source', 'link-in-bio');
    const redirectUrl = `/doctor/${resolvedData.id}?${newParams.toString()}`;
    return <Navigate to={redirectUrl} replace />;
  }

  // Resolved as clinic - redirect to clinic profile page
  if (resolvedData?.type === 'clinic') {
    // Build the URL with any query params (like embed=true if needed in future)
    const queryString = searchParams.toString();
    const redirectUrl = `/clinic/${resolvedData.id}${queryString ? `?${queryString}` : ''}`;
    return <Navigate to={redirectUrl} replace />;
  }

  return null;
};

export default SlugResolver;
